import { Button } from "../../components/Button";
import { CustomizerModal, hasTenantCallFocusTopics, SelectButton } from "./CustomizerModal";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { ShowGenericErrorToast } from "../../services/toast";
import { api } from "../../services/api";
import { PraxisCall } from "../praxis-call/PraxisCall";
import { usePraxisCall } from "./context/praxiscall-context";
import { filterTopicsForGroupAndTag, getPersonalities, getPersonalityArray, getPractitionerTypes, getUser, parseInfoBadgeText, resolvePersonalitiesForPractitionerTag, type PersonalityItem } from "../../services/utils";
import { ReportsView } from "../praxis-call/ReportScreen";
import { useUser } from "../../services/user-context";
import { SelfEvaluationForm } from "./components/SelfEvaluationForm";
import { useApp } from "../../services/app-context";
import { PRAXIS_PLAY_EVENTS } from "../../integrations/posthog/events";
import { useAnalytics } from "../../integrations/posthog/useAnalytics";
import { Tabs } from "./components/Tabs";
import {
  applyCustomizerToCharacterForm,
  clearPractitionerFields,
  findHCPListIndexForPractitionerType,
  formMatchesHcpTagFilter,
  getOrderedHCPList,
  isPraxisPlayStartFormValid,
} from "./praxisPlayFormUtils";
import { PraxisPlayHCPScreen } from "./PraxisPlayHCPScreen";
import { PraxisPlayPracticeMaterialsScreen } from "../sales-aid/sales-aid-list/PraxisPlayPracticeMaterialsScreen";
import { useTutorial } from "../../contexts/TutorialContext";
import { TutorialTooltip } from "../../components/TutorialTooltip";
import { NotificationButton } from "../../components/NotificationButton";
import { MicPermissionModal } from "../../components/MicPermissionModal";
import { checkMicPermission, useMicPermissionWarmup } from "../../services/mic-permission";
import { useQuery } from "@tanstack/react-query";
import { twMerge } from "tailwind-merge";
import { getLoggerRef } from "../../integrations/logger/logger-bridge";
import { GAME_MODE_TOOLTIP, PRACTICE_MODE_TOOLTIP } from "../../constant";
import { isCallFocusEnabled } from "../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../integrations/posthog/usePostHogFeatureFlag";


const EMPTY_LAST_HCP: Record<string, never> = {};

function shuffleArrayOfObjects(array) {
  return array.sort(() => Math.random() - 0.5);
}

function shuffleRandomizeValues({
  practitioner_types,
  personalities,
  objections,
  lastHCP,
}: {
  practitioner_types: { type: string; image: string; key: string; tag?: string }[];
  personalities: PersonalityItem[];
  objections: any[];
  lastHCP: any;
}) {
  const types = practitioner_types;
  const characters: any[] = [];
  const ObjectionLevels: { [key: string]: string } = {};
  objections.forEach((objection) => {
    ObjectionLevels[objection.value] = objection.value;
  });
  const lastHCPPractitionerType = lastHCP?.practitionerType;
  const lastHcpTypeIsKnown =
    lastHCPPractitionerType != null && String(lastHCPPractitionerType).length > 0;
  let lastHcpMarkerPlaced = false;
  types.forEach(({ type, image, key, tag }) => {
    // Random number between 1 and 7
    const source = Array.from({ length: 7 }, (_, i) => i + 1);

    // Randomly shuffled ObjectionLevelType
    const levelObjection =
      Object.values(ObjectionLevels).sort(() => Math.random() - 0.5)[0] || objections[0]?.value || "";
    const hcpAvailableTime = source[Math.floor(Math.random() * source.length)];
    const personalityKeys = resolvePersonalitiesForPractitionerTag(personalities, tag).map(
      (personality) => personality.key,
    );
    const shuffledPersonality = shuffleArrayOfObjects([...personalityKeys]);
    const firstTwoPersonality = shuffledPersonality[0];
    let character: any = {
      practitionerType: key || type,
      practitionerImage: image,
      practitionerTag: tag,
      objectionLevel: levelObjection,
      personalityType: firstTwoPersonality,
      hcpAvailableTime,
      topics: [],
    }
    // Without a real last-HCP type, `undefined === key` would match every row missing `key` and mark all cards.
    // If several types share the same display `type`, only the first match should carry the badge.
    if (
      lastHcpTypeIsKnown &&
      !lastHcpMarkerPlaced &&
      (lastHCPPractitionerType === key || lastHCPPractitionerType === type)
    ) {
      const lastObjectionLevel =
        lastHCP?.objectionLevel && ObjectionLevels[lastHCP.objectionLevel]
          ? lastHCP.objectionLevel
          : levelObjection;
      character.objectionLevel = lastObjectionLevel;

      const tagFilteredKeys = resolvePersonalitiesForPractitionerTag(personalities, tag).map(
        ({ key }) => key,
      );
      const lastPersonalityKey = getPersonalityArray(lastHCP?.personalityType).find(
        (key) => key && tagFilteredKeys.includes(key),
      );
      character.personalityType =
        lastPersonalityKey ?? shuffleArrayOfObjects([...tagFilteredKeys])[0];

      const rawTopics = lastHCP?.topics;
      character.topics = Array.isArray(rawTopics)
        ? rawTopics.map(String).filter(Boolean)
        : [];
      character.isLastHCP = true;
      lastHcpMarkerPlaced = true;
    }
    characters.push(character);
  });
  return characters;
}


export function PraxisPlay() {
  const user = getUser();
  const [tab, setTab] = useState("ai-hcp-selection");
  const { userInfo, loading } = useUser() as any;
  const { captureEvent } = useAnalytics();
  const {
    setCallDetails,
    callStatus,
    setCallStatus,
    showFeedback,
    setShowFeedback,
  } = usePraxisCall();
  const { tenantProfile } = useApp();
  const info_badges = tenantProfile?.info_badges || {};
  const [selectedCharacter, setSelectedCharacter] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showSelfEvaluation, setShowSelfEvaluation] = useState(false);
  const [showMicModal, setShowMicModal] = useState(false);

  const { data: lastHCPData } = useQuery({
    queryKey: ["last-hcp"],
    queryFn: () => api.getLastHCP(),
    refetchOnWindowFocus: false,
    enabled: !!userInfo?.id,
    refetchOnReconnect: false,
    staleTime: 0,
  });
  const lastHCP = lastHCPData?.data || EMPTY_LAST_HCP;

  const groupInfo = useMemo(() => {
    return tenantProfile?.groups?.find((group) => group.key === userInfo?.group);
  }, [tenantProfile, userInfo?.group]);

  const defaultHcpTag = groupInfo?.hcpTags?.[0] || "";
  const [hcpTagOverride, setHcpTag] = useState<string | null>(null);
  const hcpTag = hcpTagOverride ?? defaultHcpTag;

  // Reset the user's tag override only when their cohort actually changes.
  // Depending on `groupInfo` (an object) would reset on every tenantProfile refetch,
  // because `Array.find()` yields a new reference each time even when the data is unchanged.
  useEffect(() => {
    setHcpTag(null);
  }, [userInfo?.group]);

  const callFocusFlagOn = usePostHogFeatureFlag(isCallFocusEnabled);
  const callFocusAvailable = useMemo(
    () => callFocusFlagOn && hasTenantCallFocusTopics(tenantProfile, userInfo?.group),
    [callFocusFlagOn, tenantProfile, userInfo?.group],
  );

  // Tutorial support
  const { nextStep, skip, getCurrentStepConfig, isVisible } = useTutorial();
  const currentStep = getCurrentStepConfig();
  const [form, setForm] = useState<any>({
    beginTime: 7,
    isPatientBased: true,
    imgUrl: "character1",
    isCertified: false,
    isCertificationMode: false,
    isVideoRecording: false,
    isMockSession: false,
    isPracticeMode: false,
    topics: [],
  });

  useEffect(() => {
    if (!callFocusAvailable) {
      setForm((prev) => {
        if (!prev.topics?.length) return prev;
        return { ...prev, topics: [] };
      });
    }
  }, [callFocusAvailable]);

  // Request microphone permission early while user is on the setup screen
  useMicPermissionWarmup("PraxisPlay setup screen");

  // Build the full character list once per real input change. Tag toggling must NOT
  // be a dependency — including it would re-run shuffleRandomizeValues and re-roll
  // every card's objection level, time, and personality on each toggle. Tag filtering
  // is applied downstream in getOrderedHCPList. Personalities are passed unfiltered so
  // shuffleRandomizeValues can scope each character by its own practitioner tag via
  // resolvePersonalitiesForPractitionerTag; pre-filtering by the user-selected hcpTag
  // would double-filter personalities for practitioners whose tag differs.
  const characterList: any[] = useMemo(() => {
    if (!loading) {
      const group = userInfo?.group;
      let types = getPractitionerTypes(tenantProfile?.practitioner_types || {}, group) || [];
      const objections = tenantProfile?.objections || [];
      return shuffleRandomizeValues({
        practitioner_types: types,
        personalities: getPersonalities(tenantProfile?.personalities || {}, group),
        objections: objections,
        lastHCP: lastHCP,
      });
    }
    return [];
  }, [tenantProfile, userInfo?.group, loading, lastHCP]);

  const canBeginSession = useMemo(
    () =>
      isPraxisPlayStartFormValid({
        form,
        hcpTag,
        characterList,
        tenantProfile,
        group: userInfo?.group,
      }),
    [form, hcpTag, characterList, tenantProfile, userInfo?.group],
  );

  // Form sync with characterList. When PraxisPlayHCPScreen is mounted (tab === "ai-hcp-selection"), child owns form — parent skips to avoid overwriting user's hcpTag selection after characterList repopulates.
  // When characterList is empty, parent always clears practitioner fields (handles case when child is unmounted).
  // When child is unmounted (practice-materials tab), parent initializes form from the current tag filter.
  useEffect(() => {
    if (characterList.length === 0) {
      setForm((prev) => clearPractitionerFields(prev));
      return;
    }
    if (tab === "ai-hcp-selection") return;

    const list = getOrderedHCPList(characterList, hcpTag);
    if (list.length === 0) {
      setForm((prev) => clearPractitionerFields(prev));
      return;
    }

    if (formMatchesHcpTagFilter(form, hcpTag, list)) return;

    const initialIndex = list.length >= 2 ? 1 : 0;
    const selected = list[initialIndex] ?? list[0];
    if (selected) {
      const { isLastHCP, ...characterData } = selected;
      setForm((prev) => ({ ...prev, ...characterData }));
    }
  }, [characterList, hcpTag, tab, form.practitionerType]);

  // Enable practice mode when tutorial step 3 is active
  // This ensures practice mode is enabled even if character selection changes it
  useEffect(() => {
    if (currentStep?.id === "step-3" && !form.isPracticeMode) {
      setForm((prev) => ({
        ...prev,
        isPracticeMode: true,
      }));
    }
  }, [currentStep?.id, form.isPracticeMode, form.practitionerType]);

  const handleCustomizerContinue = (customizeForm: {
    practitionerType: string;
    objectionLevel: string;
    personalityType: string;
    beginTime?: number;
    topics?: string[];
    practitionerImage?: string;
  }) => {
    const group = userInfo?.group;
    const practitionerTypes =
      getPractitionerTypes(tenantProfile?.practitioner_types || {}, group) || [];
    const list = getOrderedHCPList(characterList, hcpTag);
    const matchIndex = findHCPListIndexForPractitionerType(
      characterList,
      hcpTag,
      customizeForm.practitionerType,
      practitionerTypes,
    );
    const index = matchIndex >= 0 ? matchIndex : selectedCharacter;
    const matchedCharacter = list[index];

    setSelectedCharacter(index);
    setForm((prev) =>
      applyCustomizerToCharacterForm(prev, matchedCharacter, customizeForm),
    );
    setShowCustomizer(false);
  };

  const handleCallStarted = (data) => {
    captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_STARTED, data);
  };

  const isBeginningRef = useRef(false);

  const handleBegin = async () => {
    if (!canBeginSession || isBeginningRef.current) return;
    isBeginningRef.current = true;

    const micGranted = await checkMicPermission();
    if (!micGranted) {
      setShowMicModal(true);
      isBeginningRef.current = false;
      return;
    }

    try {
      setSaving(true);
      // Ensure practice mode is enabled if tutorial step 3 is active
      const finalForm = currentStep?.id === "step-3"
        ? { ...form, isPracticeMode: true }
        : form;

      // save form data
      const payload = {
        ...finalForm,
        userId: userInfo?.id || userInfo?.["sub"],
        isLastHCP: undefined,
      };
      if (!callFocusAvailable) {
        delete payload.topics;
      } else {
        payload.topics = filterTopicsForGroupAndTag(
          payload.topics,
          tenantProfile,
          userInfo?.group,
          hcpTag,
        );
      }
      const response = await api.startCall(payload);
      if ([200, 201].includes(response.status)) {
        getLoggerRef()?.info('PraxisPlay session started', {
          sessionId: response.data.sessionId,
          practitionerType: payload.practitionerType,
          personalityType: payload.personalityType,
          objectionLevel: payload.objectionLevel,
          isCertificationMode: payload.isCertificationMode,
          isPracticeMode: payload.isPracticeMode,
          isVideoRecording: payload.isVideoRecording,
        });
        setCallDetails({
          ...payload,
          ...response.data,
          id: response.data.sessionId,
        });
        handleCallStarted({
          session_id: response.data.sessionId,
          source: "api_success",
          hcp_personality: payload.personalityType,
          difficulty_level: payload.objectionLevel,
          estimated_duration: payload.hcpAvailableTime || 7,
        });
        setCallStatus("calling");
        setSaving(false);
      } else {
        setSaving(false);
      }
    } catch (error: any) {
      getLoggerRef()?.error("PraxisPlay session start failed", error as Error, { source: 'handleBegin' });
      setSaving(false);
      ShowGenericErrorToast();
    } finally {
      isBeginningRef.current = false;
    }
  };

  useEffect(() => {
    captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_INITIATED, {
      source: "navigation"
    });
  }, []);

  if (callStatus === "ended") {
    return (
      <>
        <ReportsView />
      </>
    );
  }

  if (saving || callStatus !== "idle") {
    return <PraxisCall saving={saving} />;
  }

  // if (saving) {
  //   return <PraxisLoading />;
  // }

  const modeTooltipLines = info_badges?.praxisplay_mode_info ? [parseInfoBadgeText(info_badges.praxisplay_mode_info)].flat().filter(Boolean) : [];

  const gameModeTooltip = info_badges?.praxisplay_mode_info ? modeTooltipLines.slice(-1) : GAME_MODE_TOOLTIP;
  const practiceModeTooltip = info_badges?.praxisplay_mode_info ? modeTooltipLines.slice(0, 2) : PRACTICE_MODE_TOOLTIP;

  return (
    <div className="p-4 md:p-6 !pt-[80px] pb-8 lg:!pt-4 md:flex md:flex-col md:h-[100vh] lg:h-full">
      <div className="flex items-center justify-between">
        <div className="flex-col justify-start items-start tall2:gap-2 gap-2 md:gap-4 inline-flex">
          <div data-testid="praxisplay-title" className="text-text-primary text-2xl font-bold">PraxisPlay</div>
          <div className="text-text-primary text-sm md:text-lg font-normal">
            Sharpen your product knowledge and skills with interactive sessions
            with an AI-generated character.
          </div>
        </div>
        <NotificationButton />
      </div>
      <div className="relative border-b-2 border-[#D5D5D5] my-4">
        <Tabs value={tab} onChange={setTab} />
      </div>
      {tab === "ai-hcp-selection" && <>
        <div className="md:self-center md:flex-1 tall2:h-[calc(100vh-118px)] h-auto overflow-auto w-full tall2:mt-3 mt-4 px-6 py-6 bg-white rounded-2xl shadow border flex-col justify-start items-start tall2:gap-2 gap-6 flex">
          <PraxisPlayHCPScreen
            characterList={characterList}
            selectedCharacter={selectedCharacter}
            setSelectedCharacter={setSelectedCharacter}
            form={form}
            setForm={setForm}
            tenantProfile={tenantProfile}
            userInfo={userInfo}
            groupInfo={groupInfo}
            hcpTag={hcpTag}
            setHcpTag={setHcpTag}
          />
          <div className="w-full justify-between items-start flex flex-col md:flex-row gap-2">
            <div className="flex-1">
              <div className="gap-2 flex !h-12 bg-gray-100 w-fit items-center justify-center rounded-full">
                <SelectButton
                  title="Game Mode"
                  className={twMerge("!px-[18px] !h-[37px] !border-0 hover:!shadow-none !font-semibold", form.isPracticeMode !== true ? "!bg-PraxisTerracotta !text-white" : "")}
                  onClick={() => setForm((prev) => ({ ...prev, isPracticeMode: false }))}
                  selected={form.isPracticeMode !== true}
                  tooltip={gameModeTooltip}
                />
                <SelectButton
                  title="Practice Mode"
                  className={twMerge("!px-[18px] !h-[37px] !border-0 hover:!shadow-none !font-semibold", form.isPracticeMode === true ? "!bg-PraxisTerracotta !text-white" : "")}
                  onClick={() => setForm((prev) => ({ ...prev, isPracticeMode: true }))}
                  selected={form.isPracticeMode === true}
                  tooltip={practiceModeTooltip}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                className={`w-full md:w-auto px-7 !h-12 hover:!opacity-70 !border-0 !rounded-full !text-[15px] !font-semibold !text-[#090909] !bg-background2`}
                onClick={() => setShowCustomizer(true)}
              >
                Customize AI HCP
              </Button>
              <Button
                variant="primary"
                className="w-full md:w-auto px-7 !h-12 !rounded-full !text-[15px] !font-semibold"
                onClick={handleBegin}
                loading={saving}
                disabled={saving || !canBeginSession}
              >
                Begin PraxisPlay
              </Button>
            </div>
          </div>
        </div>
      </>}
      {tab === "practice-materials" && <>
        <PraxisPlayPracticeMaterialsScreen />
        <div className="flex justify-end mt-4">
          <Button
            variant="primary"
            className="w-full md:w-auto px-7 !h-12 !rounded-full !text-[15px] !font-semibold"
            onClick={handleBegin}
            loading={saving}
            disabled={saving || !canBeginSession}
          >
            Begin PraxisPlay
          </Button>
        </div>
      </>}
      <AnimatePresence mode="wait" initial={false}>
        {showCustomizer && (
          <CustomizerModal
            data={form}
            hcpTag={hcpTag}
            isOpen={showCustomizer}
            onClose={() => setShowCustomizer(false)}
            onContinue={handleCustomizerContinue}
            groupInfo={groupInfo}
            setHcpTag={setHcpTag}
          />
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait" initial={false}>
        {showSelfEvaluation && (
          <SelfEvaluationForm
            isOpen={showSelfEvaluation}
            onClose={() => setShowSelfEvaluation(false)}
          />
        )}
      </AnimatePresence>
      <MicPermissionModal
        isOpen={showMicModal}
        onClose={() => setShowMicModal(false)}
        productName="PraxisPlay"
        purpose="conduct AI roleplay sessions"
      />
      <TutorialTooltip
        step={currentStep}
        isVisible={isVisible}
        onAction={nextStep}
        onSkip={skip}
      />
    </div>
  );
}
